<template>
  <div class="renderPage fe-container" :class="{ 'theme--dark': $vuetify.theme.dark }">

    <!-- TOOLBAR CABECERA -->
    <v-toolbar flat height="56" :color="$vuetify.theme.dark ? '#1e293b' : '#f1f5f9'" class="fe-toolbar px-2">
      <v-icon class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-file-document-edit-outline</v-icon>
      <v-toolbar-title class="font-weight-black text-uppercase fe-toolbar-title" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-4'">
        Facturación Electrónica
      </v-toolbar-title>
      <v-spacer />
      <v-btn depressed color="primary" class="fe-btn font-weight-bold" @click="validarLicencia()">
        <v-icon left small>mdi-plus</v-icon> Nueva Factura
      </v-btn>
    </v-toolbar>

    <!-- TABLA -->
    <v-card flat class="fe-table-card mx-0" :color="$vuetify.theme.dark ? '#0f172a' : 'white'">
      <v-toolbar flat height="auto" :color="$vuetify.theme.dark ? '#0f172a' : 'white'" class="px-3 pt-2 pb-3">
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="3">
            <v-dialog
              ref="dialog"
              v-model="dateModal"
              :return-value.sync="dates"
              persistent
              width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="datesFormat"
                  label="Rango de fechas"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  hide-details
                  class="fe-search"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="dates" range scrollable color="primary">
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="dateModal = false">Cancelar</v-btn>
                <v-btn text color="primary" @click="$refs.dialog.save(dates); setDatesFormat(); getData();">OK</v-btn>
              </v-date-picker>
            </v-dialog>
          </v-col>
          <v-col cols="12" sm="5" md="4">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Buscar por cliente, código o estado..."
              single-line
              hide-details
              outlined
              dense
              class="fe-search"
            ></v-text-field>
          </v-col>
          <v-spacer></v-spacer>
          <v-btn icon :color="$vuetify.theme.dark ? 'blue-grey lighten-2' : 'grey darken-1'" @click="getData()">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-row>
      </v-toolbar>

      <v-data-table
        :headers="headers"
        :items="items"
        :search="search"
        class="fe-table"
        :loading="loading"
        @click:row="editItem"
        style="cursor:pointer"
      >
        <template v-slot:[`item.estadoHacienda`]="{ item }">
          <v-chip
            small
            :color="getEstadoColor(item.estadoHacienda)"
            class="font-weight-bold"
            text-color="white"
          >
            {{ item.estadoHacienda }}
          </v-chip>
        </template>

        <template v-slot:[`item.totalComprobante`]="{ item }">
          <span class="font-weight-bold">{{ (item.totalComprobante || 0).toLocaleString('es-CR', { style: 'currency', currency: 'CRC' }) }}</span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn color="blue-grey darken-2" depressed dark
            style="min-width:36px;height:36px;padding:0;border-radius:4px"
            @click.stop="editItem(item)">
            <v-icon size="18">mdi-eye-outline</v-icon>
          </v-btn>
        </template>

        <template v-slot:no-data>
          <div class="pa-8 text-center">
            <v-icon size="48" :color="$vuetify.theme.dark ? 'blue-grey darken-1' : 'grey lighten-1'" class="mb-3">mdi-file-document-outline</v-icon>
            <p :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">No se encontraron facturas en este periodo.</p>
            <v-btn color="primary" depressed small @click="getData()">Reintentar</v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- FORM DRAWER - "SUPER DRAWER" PARA FACTURACIÓN -->
    <v-navigation-drawer
      v-model="drawerForm"
      fixed
      right
      temporary
      :width="$vuetify.breakpoint.mdAndUp ? 'calc(100% - 290px)' : '100%'"
      class="fe-drawer"
      stateless
    >
      <div class="d-flex flex-column fill-height">
        <!-- HEADER DEL DRAWER -->
        <div class="fe-drawer-header d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'fe-drawer-header--dark' : ''">
          <v-icon color="white" class="mr-3">mdi-file-document-edit-outline</v-icon>
          <div>
            <div class="white--text font-weight-black text-uppercase" style="letter-spacing:1px;font-size:0.95rem;">
              {{ editedIndex === -1 ? 'Nueva Factura Electrónica' : 'Detalles de Factura' }}
            </div>
            <div class="caption white--text" style="opacity:0.7">
              {{ editedItem.codigoFact ? 'Comprobante #' + editedItem.codigoFact : 'Generando nuevo documento...' }}
              <v-chip v-if="editedIndex > -1 && editedItem.estadoHacienda" x-small dark outlined class="ml-2 font-weight-black" style="height:18px">
                {{ editedItem.estadoHacienda }}
              </v-chip>
            </div>
          </div>
          <v-spacer />
          <v-btn icon dark small @click="cerrar()"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <!-- CONTENIDO DEL DRAWER -->
        <div class="flex-grow-1 overflow-y-auto px-2 py-2 fe-drawer-body">
          <v-form v-model="valid" ref="form">
            <v-expansion-panels v-model="activePanels" multiple class="fe-panels">
              
              <!-- SECCIÓN 1: SELECCIÓN DE CLIENTE -->
              <v-expansion-panel class="mb-0 elevation-0 fe-panel">
                <v-expansion-panel-header class="font-weight-black text-caption text-uppercase fe-panel-header">
                  <v-icon color="primary" left class="mr-2">mdi-account-search-outline</v-icon>
                  Información del Cliente
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="# Código Cliente"
                        v-model="editedItem.clienteCod"
                        outlined dense hide-details
                        @input="iconVisible = true"
                        :disabled="!incluirValid"
                      >
                        <template v-slot:append>
                          <v-icon color="primary" @click="BuscarClienteCodigo" v-if="iconVisible && incluirValid">mdi-magnify</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Cédula / Identificación"
                        v-model="editedItem.clienteCedula"
                        outlined dense hide-details
                        @input="iconVisibleCedula = true"
                        :disabled="!incluirValid"
                      >
                        <template v-slot:append>
                          <v-icon color="primary" @click="BuscarClienteCedula" v-if="iconVisibleCedula && incluirValid">mdi-magnify</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="1" class="text-center">
                      <v-btn color="primary" icon large @click="mostrarBuscarClientes" :disabled="!btnAgregarCliente">
                        <v-icon>mdi-account-plus</v-icon>
                      </v-btn>
                    </v-col>
                    <v-col cols="12" md="5">
                      <v-text-field
                        v-model="editedItem.clienteNombre"
                        label="Nombre Completo"
                        outlined dense hide-details
                        readonly
                        placeholder="Seleccione un cliente..."
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" v-if="actividadesCliente.length > 0">
                      <v-select
                        v-model="editedItem.clienteCodigoActividad"
                        :items="actividadesCliente"
                        item-text="descripcion"
                        item-value="codigo"
                        label="Actividad Económica del Receptor"
                        outlined dense hide-details
                        prepend-inner-icon="mdi-briefcase-outline"
                        :disabled="!incluirValid"
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- SECCIÓN 2: LÍNEAS DE DETALLE -->
              <v-expansion-panel class="mb-0 elevation-0 fe-panel">
                <v-expansion-panel-header class="font-weight-black text-caption text-uppercase fe-panel-header">
                  <div class="d-flex align-center w-100">
                    <v-icon color="primary" left class="mr-2">mdi-format-list-bulleted</v-icon>
                    Detalle de la Factura
                    <v-spacer></v-spacer>
                    <v-btn
                      v-if="btnAgregarArti"
                      color="success"
                      x-small
                      depressed
                      dark
                      @click.stop="mostrarBuscarArticulos"
                      class="mr-4 px-3 fe-btn"
                    >
                      <v-icon left size="16">mdi-cart-plus</v-icon>
                      Añadir Artículo
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="pt-0">

                  <v-data-table
                    :headers="headersDetalle"
                    :items="lineasDetalle"
                    hide-default-footer
                    class="fe-detail-table edit-table"
                    :items-per-page="-1"
                  >
                    <template v-slot:[`item.cantidad`]="{ item }">
                      <v-text-field
                        v-model.number="item.cantidad"
                        outlined rounded hide-details
                        class="compact-input"
                        @input="recalcularTotales"
                      ></v-text-field>
                    </template>
                    <template v-slot:[`item.montoStr`]="{ item }">
                      <span class="font-weight-medium text-subtitle-1">{{ item.montoStr || (item.monto).toLocaleString('es-CR', { style: 'currency', currency: 'CRC' }) }}</span>
                    </template>
                    <template v-slot:[`item.porcDescuento`]="{ item }">
                      <v-text-field
                        v-model.number="item.porcDescuento"
                        outlined rounded hide-details
                        class="compact-input"
                        @input="recalcularTotales"
                      ></v-text-field>
                    </template>
                    <template v-slot:[`item.totalLineaStr`]="{ item }">
                      <span class="font-weight-medium text-subtitle-1">{{ item.totalLineaStr || (item.cantidad * item.monto).toLocaleString('es-CR', { style: 'currency', currency: 'CRC' }) }}</span>
                    </template>
                    <template v-slot:[`item.edit`]="{ item }">
                      <v-btn icon small color="primary" @click="detalleEditItem(item)" v-if="guardarValid">
                        <v-icon small>mdi-pencil-outline</v-icon>
                      </v-btn>
                    </template>
                    <template v-slot:[`item.delete`]="{ item }">
                      <v-btn icon small color="error" @click="deleteItem(item)" v-if="guardarValid">
                        <v-icon small>mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- SECCIÓN 3: RESUMEN Y TOTALES -->
              <v-expansion-panel class="mb-0 elevation-0 fe-panel">
                <v-expansion-panel-header class="font-weight-black text-caption text-uppercase fe-panel-header">
                  <v-icon color="primary" left class="mr-2">mdi-calculator-variant-outline</v-icon>
                  Resumen de Totales
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense>
                    <v-col cols="12" md="3">
                      <v-card flat class="fe-total-card pa-2 text-center">
                        <div class="caption grey--text mb-0">Subtotal</div>
                        <div class="text-body-2 font-weight-bold">{{ editedItem.subtotalStr || '0.00' }}</div>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-card flat class="fe-total-card pa-2 text-center">
                        <div class="caption grey--text mb-0">Descuentos</div>
                        <div class="text-body-2 font-weight-bold error--text">{{ editedItem.totalDescuentosStr || '0.00' }}</div>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-card flat class="fe-total-card pa-2 text-center">
                        <div class="caption grey--text mb-0">Impuestos</div>
                        <div class="text-body-2 font-weight-bold primary--text">{{ editedItem.totalImpuestoStr || '0.00' }}</div>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-card flat class="fe-total-card fe-total-card--primary pa-2 text-center white--text">
                        <div class="caption opacity-80 mb-0">TOTAL FACTURA</div>
                        <div class="text-body-2 font-weight-black">{{ editedItem.totalComprobanteStr || '0.00' }}</div>
                      </v-card>
                    </v-col>
                  </v-row>

                  <v-row class="mt-2 border-top-light pt-2" dense>
                    <v-col cols="12" md="2">
                      <v-text-field v-model="editedItem.totalIva1Str" label="IVA 1%" readonly outlined dense hide-details></v-text-field>
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-text-field v-model="editedItem.totalIva2Str" label="IVA 2%" readonly outlined dense hide-details></v-text-field>
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-text-field v-model="editedItem.totalIva4Str" label="IVA 4%" readonly outlined dense hide-details></v-text-field>
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-text-field v-model="editedItem.totalIva13Str" label="IVA 13%" readonly outlined dense hide-details></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                       <v-text-field v-model="editedItem.totalIVADevueltoStr" label="IVA Devuelto" readonly outlined dense hide-details class="right-input"></v-text-field>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- SECCIÓN 4: HACIENDA Y ACCIONES -->
              <v-expansion-panel class="mb-0 elevation-0 fe-panel">
                <v-expansion-panel-header class="font-weight-black text-caption text-uppercase fe-panel-header">
                  <div class="d-flex align-center" style="width:100%">
                    <v-icon color="primary" left class="mr-2">mdi-cloud-check-outline</v-icon>
                    Estado y Gestión Hacienda
                    <v-spacer></v-spacer>
                    <v-chip
                      v-if="editedItem.estadoHacienda"
                      :color="getEstadoColor(editedItem.estadoHacienda)"
                      dark x-small
                      class="font-weight-bold mr-2"
                    >
                      {{ editedItem.estadoHacienda }}
                    </v-chip>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense align="start">
                    <v-col cols="12" md="8">
                      <v-text-field
                        v-model="editedItem.clavehacienda"
                        label="Clave de Comprobante"
                        readonly outlined dense hide-details
                        :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center">
                       <v-btn
                        color="primary"
                        depressed
                        block
                        height="40"
                        class="fe-btn font-weight-bold"
                        @click="consultarEstadoHacienda"
                        :disabled="!consultarEstadoValid || visibleProgress"
                        :loading="visibleProgress"
                      >
                        <v-icon left>mdi-sync</v-icon>
                        Consultar Estado
                      </v-btn>
                    </v-col>
                    <v-col cols="12" v-if="editedItem.estadoHacienda" class="mt-2">
                       <div class="d-flex align-center pa-2 fe-total-card">
                         <v-icon :color="getEstadoColor(editedItem.estadoHacienda)" class="mr-2">mdi-information-outline</v-icon>
                         <span class="font-weight-bold mr-2">Estado:</span>
                         <v-chip dark small :color="getEstadoColor(editedItem.estadoHacienda)" class="font-weight-black">
                           {{ editedItem.estadoHacienda }}
                         </v-chip>
                       </div>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea
                        v-model="editedItem.mensajeHacienda"
                        label="Respuesta de Hacienda"
                        readonly outlined rounded rows="2"
                        class="mt-2"
                        hide-details
                      ></v-textarea>
                    </v-col>
                  </v-row>

                  <v-row v-if="editedIndex > -1" class="border-top-light pt-4 px-2" justify="center">
                    <v-btn text color="primary" class="mx-2 fe-btn" @click="imprimir" :disabled="!verPDF">
                      <v-icon left>mdi-file-pdf-box</v-icon> Ver PDF
                    </v-btn>
                    <v-btn text color="primary" class="mx-2 fe-btn" @click="getXMLFirmado" :disabled="!verXMLFirmado">
                      <v-icon left>mdi-xml</v-icon> XML Enviado
                    </v-btn>
                    <v-btn text color="primary" class="mx-2 fe-btn" @click="getXMLRespuesta" :disabled="!verXMLRespuesta">
                      <v-icon left>mdi-message-reply-text</v-icon> Respuesta XML
                    </v-btn>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- SECCIÓN 5: CONFIGURACIÓN ADICIONAL -->
              <v-expansion-panel class="mb-0 elevation-0 fe-panel">
                <v-expansion-panel-header class="font-weight-black text-caption text-uppercase fe-panel-header">
                  <v-icon color="primary" left class="mr-2">mdi-cog-outline</v-icon>
                  Parámetros de Emisión
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense>
                    <v-col cols="12" md="3">
                      <v-select label="Moneda" v-model="editedItem.moneda" :items="monedas" item-text="md_nombre" item-value="md_codigo" @change="changeMonedas" outlined dense hide-details prepend-inner-icon="mdi-cash-multiple"></v-select>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field v-model="editedItem.tipoCambio" label="T. Cambio" type="number" outlined dense hide-details></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-select label="Condición Venta" v-model="editedItem.condicionVenta" :items="condicionesVenta" item-text="descripcion" item-value="codigo" outlined dense hide-details></v-select>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-select label="Medio de Pago" v-model="editedItem.medioPago" :items="mediosPago" item-text="descripcion" item-value="codigo" outlined dense hide-details></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="editedItem.codigoActividad"
                        :items="actividades"
                        item-text="descripcion"
                        item-value="codigo"
                        label="Actividad Económica del Emisor"
                        outlined dense hide-details
                        prepend-inner-icon="mdi-briefcase-outline"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field v-model="editedItem.ordenCompra" label="Orden de Compra #" outlined dense prepend-inner-icon="mdi-file-document-outline" hide-details></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea v-model="editedItem.observacion" label="Observaciones en factura" outlined rounded rows="2" hide-details></v-textarea>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- SECCIÓN CONTINGENCIA -->
              <v-expansion-panel class="mb-0 elevation-0 fe-panel">
                <v-expansion-panel-header class="font-weight-black text-caption text-uppercase fe-panel-header">
                  <div class="d-flex align-center" style="width:100%">
                    <v-icon color="warning" left class="mr-2">mdi-alert-circle-outline</v-icon>
                    <span class="warning--text">Contingencia</span>
                    <v-chip x-small color="warning" outlined class="ml-3 font-weight-bold">Uso excepcional</v-chip>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-text-field v-model="editedItem.contingenciaFact" label="N° Comprobante Físico" outlined dense prepend-inner-icon="mdi-file-alert-outline" hide-details></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field v-model="editedItem.contingenciaFecha" label="Fecha Emisión Física" type="datetime-local" outlined dense prepend-inner-icon="mdi-calendar-clock" hide-details></v-text-field>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-form>
        </div>

        <!-- FOOTER ACCIONES -->
        <div class="fe-drawer-footer d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'fe-drawer-footer--dark' : ''">
          <v-btn v-if="reenviarValid" color="secondary" text class="fe-btn font-weight-bold" @click="reenviar">
            <v-icon left small>mdi-send-outline</v-icon> Reenviar Factura
          </v-btn>
          <v-spacer />
          <v-btn text class="mr-3 font-weight-bold fe-btn" @click="cerrar()">Cancelar</v-btn>
          <v-btn color="primary" depressed class="px-8 font-weight-bold fe-btn" :loading="visibleProgress" @click="save" :disabled="!guardarValid">
            {{ editedIndex === -1 ? 'GENERAR FACTURA' : 'GUARDAR CAMBIOS' }}
          </v-btn>
        </div>
      </div>
    </v-navigation-drawer>

    <!-- VISOR DE DOCUMENTOS (PDF / XML) -->
    <v-dialog v-model="docViewerDialog" max-width="1100" eager>
      <v-card style="height: 88vh; border-radius:4px" class="d-flex flex-column overflow-hidden">
        <div class="fe-drawer-header d-flex align-center px-4 py-3 flex-shrink-0" :class="$vuetify.theme.dark ? 'fe-drawer-header--dark' : ''">
          <v-icon color="white" size="18" class="mr-2">mdi-file-document-outline</v-icon>
          <span class="white--text font-weight-black text-uppercase" style="letter-spacing:1px;font-size:0.9rem;">{{ docViewerTitle }}</span>
          <v-spacer></v-spacer>
          <v-btn icon x-small dark class="mr-1" @click="abrirEnNuevaPestana" title="Abrir en nueva pestaña">
            <v-icon small>mdi-open-in-new</v-icon>
          </v-btn>
          <v-btn icon x-small dark @click="docViewerDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
        <iframe v-if="docViewerDialog" :src="docViewerUrl" class="flex-grow-1" style="border: none; width: 100%;"></iframe>
      </v-card>
    </v-dialog>

    <!-- MODALES DE BÚSQUEDA (MODERNIZADOS) -->
    <v-dialog transition="dialog-bottom-transition" v-model="dialog" width="900" persistent>
      <div v-if="showNewData">
        <BuscarClientes v-bind:allwedOnly="true" @clicked="selected" @closed="closed" />
      </div>
    </v-dialog>

    <v-dialog transition="dialog-bottom-transition" v-model="dialogBuscarArticulos" width="1000" persistent>
      <div v-if="showNewDataArti">
        <BuscarArticulos v-bind:allwedOnly="true" @clicked="selectedArticulos" @closed="closed" />
      </div>
    </v-dialog>

    <!-- MODAL DETALLE ARTÍCULO -->
    <v-dialog v-model="dialogArticulos" max-width="700px" persistent>
      <v-card flat class="fe-dialog-card overflow-hidden">
        <div class="fe-drawer-header d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'fe-drawer-header--dark' : ''">
          <v-icon color="white" class="mr-3">mdi-cart-edit</v-icon>
          <div class="white--text font-weight-black text-uppercase" style="letter-spacing:1px;font-size:0.95rem;">{{ formTitle }}</div>
          <v-spacer></v-spacer>
          <v-btn icon dark small @click="dialogArticulos = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <div class="pa-5">
          <v-row dense>
            <v-col cols="12" md="4">
              <v-text-field v-model="detalleEditedItem.articulo" label="Código" outlined dense readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="8">
              <v-text-field v-model="detalleEditedItem.descripcion" label="Descripción" outlined dense></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="detalleEditedItem.cantidad" label="Cantidad" type="number" outlined dense step="any"></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="detalleEditedItem.monto" label="Precio Unit." type="number" outlined dense step="any"></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="detalleEditedItem.porcDescuento" label="% Descuento" type="number" outlined dense></v-text-field>
            </v-col>
            <v-col cols="12">
               <v-text-field v-model="detalleEditedItem.cabys" label="Código CABYS" outlined dense readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="detalleEditedItem.registroMedicamento" label="Reg. Medicamento" outlined dense></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="detalleEditedItem.formaFarmaceutica" :items="formas" item-text="descripcion" item-value="codigo" label="Forma Farmacéutica" outlined dense></v-select>
            </v-col>
          </v-row>
          <div class="fe-drawer-footer d-flex justify-end mt-2" :class="$vuetify.theme.dark ? 'fe-drawer-footer--dark' : ''">
            <v-btn text class="mr-3 font-weight-bold fe-btn" @click="dialogArticulos = false">Cancelar</v-btn>
            <v-btn color="primary" depressed class="px-8 font-weight-bold fe-btn" @click="saveDetalleArticulo">Aceptar</v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- OTROS DIALOGOS -->
    <v-dialog transition="dialog-bottom-transition" v-model="dialogSinpe">
      <div v-if="showNewData"><PagoSinpe v-bind:allwedOnly="true" v-bind:pagoInfo="pagoInfo" @closed="closed" /></div>
    </v-dialog>

    <!-- MODAL REENVÍO POR CORREO -->
    <v-dialog v-model="dialogFormaReenvio" max-width="500px">
      <v-card flat class="fe-dialog-card overflow-hidden">
        <div class="fe-drawer-header d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'fe-drawer-header--dark' : ''">
          <v-icon color="white" class="mr-3">mdi-email-send-outline</v-icon>
          <div class="white--text font-weight-black text-uppercase" style="letter-spacing:1px;font-size:0.95rem;">Reenviar Comprobante</div>
          <v-spacer></v-spacer>
          <v-btn icon dark small @click="dialogFormaReenvio = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <div class="pa-5">
          <p class="body-2 mb-4" :class="$vuetify.theme.dark ? 'blue-grey--text text--lighten-1' : 'grey--text text--darken-1'">Confirme o edite el correo electrónico para el reenvío:</p>
          <v-text-field
            v-model="reenvioInfo.correo"
            label="Correo Electrónico"
            outlined dense
            prepend-inner-icon="mdi-email-outline"
            hide-details
          ></v-text-field>
          <div class="fe-drawer-footer d-flex justify-end mt-4" :class="$vuetify.theme.dark ? 'fe-drawer-footer--dark' : ''">
            <v-btn text class="mr-3 font-weight-bold fe-btn" @click="dialogFormaReenvio = false">Cancelar</v-btn>
            <v-btn color="secondary" depressed class="px-8 font-weight-bold fe-btn" @click="confirmarReenvio">
              Enviar Ahora <v-icon right small>mdi-send</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogDelete" max-width="400px">
      <v-card flat class="fe-dialog-card">
        <div class="fe-drawer-header d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'fe-drawer-header--dark' : ''">
          <v-icon color="white" class="mr-3">mdi-alert-circle-outline</v-icon>
          <div class="white--text font-weight-black text-uppercase" style="letter-spacing:1px;font-size:0.95rem;">¿Eliminar línea?</div>
          <v-spacer></v-spacer>
          <v-btn icon dark small @click="closeDelete"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <div class="pa-5">
          <p class="body-2 mb-4" :class="$vuetify.theme.dark ? 'blue-grey--text text--lighten-1' : 'grey--text text--darken-1'">¿Estás seguro de que deseas descartar este artículo de la factura?</p>
          <div class="d-flex justify-end">
            <v-btn text class="mr-3 font-weight-bold fe-btn" @click="closeDelete">Cancelar</v-btn>
            <v-btn color="error" depressed class="px-6 font-weight-bold fe-btn" @click="deleteItemConfirm">Eliminar</v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <Snackbar v-bind:text="snackText" v-bind:snackbar.sync="snackValue" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Snackbar from "./shared/Snackbar.vue";
import BuscarClientes from "./BuscarClientes.vue";
import BuscarArticulos from "./BuscarArticulos.vue";
import PagoSinpe from "./PagoSinpe.vue";

export default {
  components: {
    Snackbar,
    BuscarClientes,
    BuscarArticulos,
    PagoSinpe,
  },
  data() {
    return {
      drawerForm: false,
      activePanels: [0, 1],
      loading: false,
      btnAgregarCliente: false,
      btnAgregarArti: false,
      iconVisible: false,
      iconVisibleCedula: false,
      visibleBtnConsultar: true,
      visibleProgress: false,
      dialog: false,
      dialogArticulos: false,
      dialogSinpe: false,
      dialogFormaReenvio: false,
      dialogBuscarArticulos: false,
      valid: false,
      incluirValid: true,
      consultarEstadoValid: false,
      cancelarValid: false,
      reenviarValid: false,
      imprimirValid: false,
      docViewerDialog: false,
      docViewerUrl: '',
      docViewerTitle: '',
      guardarValid: false,
      search: "",
      snackText: "",
      snackValue: false,
      dialogDelete: false,
      verPDF: false,
      verXMLFirmado: false,
      verXMLRespuesta: false,
      modal: false,
      dateModal: false,
      dates: [],
      datesFormat: [],
      panel: [],
      panelResumen: [],
      fecha: "",
      headers: [
        { text: "Código", align: "start", value: "codigoFact" },
        { text: "Cliente", align: "start", value: "clienteNombre" },
        { text: "Fecha", align: "start", value: "fechaString" },
        { text: "Estado", align: "start", value: "estadoHacienda" },
        { text: "Total", align: "end", value: "totalComprobante" },
        { text: "Acciones", value: "actions", align: 'right', width: 50, sortable: false },
      ],
      headersDetalle: [
        { text: "Cant", align: "start", value: "cantidad", width: 140 },
        { text: "Código", align: "start", value: "articulo", width: 120 },
        { text: "Descripción", align: "start", value: "descripcion" },
        { text: "Precio", align: "start", value: "montoStr", width: 120 },
        { text: "% Desc.", align: "start", value: "porcDescuento", width: 140 },
        { text: "Total", align: "start", value: "totalLineaStr", width: 140 },
        { text: "", value: "edit", width: 40, sortable: false },
        { text: "", value: "delete", width: 40, sortable: false },
      ],
      items: [],
      tiposIdentificacion: [],
      actividades: [],
      actividadesCliente: [],
      condicionesVenta: [],
      mediosPago: [],
      sucursales: [],
      terminales: [],
      monedas: [],
      provincias: [],
      cantones: [],
      distritos: [],
      barrios: [],
      totales: [],
      formas: [],
      lineasDetalle: [],
      tipoCambio: [],
      respuestaHacienda: [],
      editedIndex: -1,
      detalleEditedIndex: -1,
      modelFactura: {
        comprobante: [],
        comprobanteDetalle: [],
      },
      editedItem: this.getInitialItem(),
      defaultItem: this.getInitialItem(),
      detalleEditedItem: this.getInitialDetalleItem(),
      detalleDefaultItem: this.getInitialDetalleItem(),
      pagoInfo: { sucursal: "", terminal: "", total: 0 },
      reenvioInfo: { numero: "", correo: "", porwhatsapp: false, porcorreo: false },
      opcionesSistemaCia: [],
      showNewData: false,
      showNewDataArti: false,
      nameRules: [(v) => !!v || "Campo requerido"],
    };
  },

  computed: {
    ...mapGetters(["user", "strCia"]),
    formTitle() {
      return this.detalleEditedIndex === -1 ? "Añadir Artículo" : "Editar Artículo";
    },
  },

  watch: {
    search() { this.snackValue = false; },
    lineasDetalle: {
      handler(newVal) {
        if (newVal.length > 0) {
          if (!this.activePanels.includes(2)) {
            this.activePanels.push(2);
          }
        } else {
          this.activePanels = this.activePanels.filter(p => p !== 2);
        }
      },
      deep: true
    }
  },

  created() {
    this.setDates();
    this.getData();
    this.getMonedas();
    this.getProvincias();
    this.getActividades();
    this.getCondicionVenta();
    this.getMedioPago();
    this.getFormas();
    this.getOpcionesSistemaCia();
  },

  methods: {
    getInitialItem() {
      return {
        cia: "", codigoFact: "", tipoFact: "01", fecha: new Date().toISOString().slice(0, 10), tipoCambio: 1,
        clienteCod: "", clienteNombre: "", clienteCorreo: "", clientecorreo: "", clienteCedula: "", clienteAreaTel: "", clienteTelefono: "",
        clienteNombreComercial: "", clienteProvincia: "", clienteCanton: "", clienteDistrito: "", clienteBarrio: "",
        clienteDireccion: "", clienteTipoId: "", mensajeHacienda: "", moneda: "CRC", estadoHacienda: "PENDIENTE", clavehacienda: "",
        subtotal: 0, totalDescuentos: 0, totalImpuesto: 0, totalComprobante: 0, sucursal: "", terminal: "",
        observacion: "", usuario: "", nombreTabla: "Facturas", 
        fechaHacienda: new Date(), contingenciaFecha: "", contingenciaFact: "",
        totalIva1: 0, totalIva2: 0, totalIva4: 0, totalIva8: 0, totalIva13: 0, totalIVADevuelto: 0,
        subtotalStr: "0.00", totalDescuentosStr: "0.00", totalImpuestoStr: "0.00", totalComprobanteStr: "0.00",
        totalIva1Str: "0.00", totalIva2Str: "0.00", totalIva4Str: "0.00", totalIva13Str: "0.00", totalIVADevueltoStr: "0.00",
        condicionVenta: "", medioPago: "", codigoActividad: "", clienteCodigoActividad: "", emisorNombre: "", emisorNombreComercial: "",
        emisorTelefono: "", emisorCorreo: "", emisorDireccion: "", emisorCedula: "", emisorTipoId: "",
        totalServGravados: 0, totalServExentos: 0, totalServExonerado: 0, totalMercanciasGravadas: 0,
        totalMercanciasExentas: 0, totalMercExonerada: 0, totalGravado: 0, totalExento: 0, totalExonerado: 0,
        totalVenta: 0, totalVentaNeta: 0, totalOtrosCargos: 0, plazoCredito: 0, estadoDev: 0,
        codigoDev: "", consHacienda: "", consHacReal: "", xml: "", xmlFirmado: "", respuestaHacienda: "",
        cedulaReceptor: "", ordenCompra: "", enviado: false, reenviado: false
      };
    },
    getInitialDetalleItem() {
      return {
        cia: "", factura: "", tipoFact: "01", numLinea: 0, cantidad: 0, articulo: "", descripcion: "",
        unidad: "", monto: 0, descuento: 0, totalLinea: 0, impuestosVentas: 0, sucursal: "", terminal: "",
        tipoCodigo: "01", unidadMedidaComercial: "M2", impCodigo: "", impCodigoTarifa: "", impTarifa: 0,
        cabys: "", porcDescuento: 0, tipoCambio: 1, tipoProducto: "", montoTotal: 0, subTotal: 0,
        impuestoNeto: 0, moneda: "CRC", montoStr: "0.00", totalLineaStr: "0.00", clienteCod: "", clienteCedula: "",
        registroMedicamento: "", formaFarmaceutica: ""
      };
    },
    getEstadoColor(estado) {
      if (!estado) return "grey";
      const e = estado.toUpperCase();
      if (e === "ACEPTADO") return "success";
      if (e === "RECHAZADO") return "error";
      if (e === "PROCESANDO") return "warning";
      if (e === "PENDIENTE") return "info";
      return "grey";
    },

    getData() {
      let me = this;
      this.loading = true;
      axios
        .get(`api/Facturacion/GetComprobantes?cia=${this.strCia}&docNombre=Facturas&tipoDoc=01&fechaInicio=${this.dates[0]}&fechaFin=${this.dates[1]}`)
        .then(response => { me.items = response.data; })
        .catch(error => console.log(error))
        .finally(() => { me.loading = false; });
    },

    setDates() {
      const date = new Date();
      const dateInicio = new Date(date.getFullYear(), date.getMonth(), 1);
      this.dates = [dateInicio.toISOString().substr(0, 10), date.toISOString().substr(0, 10)];
      this.setDatesFormat();
    },

    setDatesFormat() {
      const format = d => {
        const [y, m, day] = d.split('-');
        return `${day}-${m}-${y}`;
      };
      this.datesFormat = `${format(this.dates[0])} / ${format(this.dates[1])}`;
    },

    getActividades() {
      axios.get("api/General/getActividadesByCia?cia=" + this.strCia)
        .then(res => this.actividades = res.data);
    },

    getCondicionVenta() {
      axios.get("api/General/getCondicionVenta")
        .then(res => {
          this.condicionesVenta = res.data;
          if (!this.editedItem.condicionVenta && this.condicionesVenta.length > 0) {
            this.editedItem.condicionVenta = this.condicionesVenta[0].codigo;
          }
        });
    },

    getMedioPago() {
      axios.get("api/General/getMediosPago")
        .then(res => {
          this.mediosPago = res.data;
          if (!this.editedItem.medioPago && this.mediosPago.length > 0) {
            this.editedItem.medioPago = this.mediosPago[0].codigo;
          }
        });
    },

    getCia() {
      axios.get("api/Cias/getCia?ciaId=" + this.strCia)
        .then(res => {
          const cia = res.data;
          this.editedItem.emisorNombre = cia.nombre;
          this.editedItem.emisorNombreComercial = cia.nombreComercial;
          this.editedItem.codigoActividad = cia.codigoActividad;
          this.editedItem.emisorTelefono = cia.telefono;
          this.editedItem.emisorCorreo = cia.correo;
          this.editedItem.emisorDireccion = cia.direccion;
          this.editedItem.moneda = cia.moneda;
          this.editedItem.sucursal = cia.sucursal;
          this.editedItem.terminal = cia.terminal;
          this.editedItem.emisorCedula = cia.cedula;
          this.editedItem.emisorTipoId = cia.tipoId;
          this.getTerminales(cia.sucursal);
        });
    },

    getSucursales() {
      axios.get("api/Cias/getSucursalesByCia?cia=" + this.strCia)
        .then(res => this.sucursales = res.data);
    },

    getTerminales(sucursal) {
      axios.get(`api/Cias/getTerminales?cia=${this.strCia}&sucursal=${sucursal}`)
        .then(res => {
          this.terminales = res.data;
          if (!this.editedItem.terminal && this.terminales.length > 0) {
            this.editedItem.terminal = this.terminales[0].terminalId;
          }
        });
    },

    getMonedas() {
      axios.get("api/General/getMonedas").then(res => this.monedas = res.data);
    },

    getProvincias() {
      axios.get("api/General/getProvincias").then(res => this.provincias = res.data);
    },

    getCantones() {
      axios.get("api/General/getCantones?provincia=" + this.editedItem.clienteProvincia)
        .then(res => this.cantones = res.data);
    },

    getDistritos() {
      axios.get(`api/General/getDistritos?provincia=${this.editedItem.clienteProvincia}&canton=${this.editedItem.clienteCanton}`)
        .then(res => this.distritos = res.data);
    },

    getBarrios() {
      axios.get(`api/General/getBarrios?provincia=${this.editedItem.clienteProvincia}&canton=${this.editedItem.clienteCanton}&distrito=${this.editedItem.clienteDistrito}`)
        .then(res => this.barrios = res.data);
    },

    BuscarClienteCodigo() {
      if (!this.editedItem.clienteCod) return;
      axios.get(`api/Clientes/getCliente?cia=${this.strCia}&codigo=${this.editedItem.clienteCod}`)
        .then(res => this.applyCliente(res.data));
    },

    BuscarClienteCedula() {
      if (!this.editedItem.clienteCedula) return;
      axios.get(`api/Clientes/getCliente?cia=${this.strCia}&cedula=${this.editedItem.clienteCedula}`)
        .then(res => this.applyCliente(res.data));
    },

    applyCliente(cliente) {
      this.editedItem.clienteCod = cliente.codigo;
      this.editedItem.clienteNombre = cliente.nombre;
      this.editedItem.clientecorreo = cliente.correo;
      this.editedItem.clienteCorreo = cliente.correo;
      this.editedItem.clienteCedula = cliente.cedula;
      this.editedItem.clienteAreaTel = cliente.areaTel;
      this.editedItem.clienteTelefono = cliente.telefono;
      this.editedItem.clienteNombreComercial = cliente.nombreComercial;
      this.editedItem.clienteProvincia = cliente.provincia;
      this.getCantones();
      this.editedItem.clienteCanton = cliente.canton;
      this.getDistritos();
      this.editedItem.clienteDistrito = cliente.distrito;
      this.getBarrios();
      this.editedItem.clienteBarrio = cliente.barrio;
      this.editedItem.clienteDireccion = cliente.direccion;
      this.editedItem.clienteTipoId = cliente.tipoId;
      this.btnAgregarArti = true;
      this.iconVisible = false;
      this.iconVisibleCedula = false;
      this.getActividadesCliente(cliente.cedula);
    },

    getActividadesCliente(cedulaCliente) {
      if (!cedulaCliente) return;
      let me = this;
      axios.get(`api/Clientes/getActividadesByCliente?cedulaCia=${this.strCia}&cedulaCliente=${cedulaCliente}`)
        .then(res => {
          me.actividadesCliente = res.data;
          if (res.data && res.data.length > 0) {
            if (!me.editedItem.clienteCodigoActividad) {
              const defecto = res.data.find(a => a.esDefecto) || res.data[0];
              me.editedItem.clienteCodigoActividad = defecto.codigo;
            }
          } else {
            me.editedItem.clienteCodigoActividad = "";
          }
        });
    },

    mostrarBuscarClientes() { this.showNewData = true; this.dialog = true; },
    mostrarBuscarArticulos() { this.showNewDataArti = true; this.dialogBuscarArticulos = true; },

    changeMonedas() {
      if (this.editedItem.moneda === "USD") this.getTipoCambio();
      else this.editedItem.tipoCambio = 1;
    },

    getTipoCambio() {
      axios.get("https://apis.gometa.org/tdc/tdc.json")
        .then(res => this.editedItem.tipoCambio = res.data.venta);
    },

    editItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.getModeloComprobante(item);
      this.drawerForm = true;
      this.activePanels = [1, 2, 3];
      this.incluirValid = true;
      this.guardarValid = false;
      this.cancelarValid = false;
    },

    getModeloComprobante(item) {
      axios.get(`api/Facturacion/getModeloComprobanteByCodigoFactura?cia=${this.strCia}&codigoFact=${item.codigoFact}&docNombre=Facturas`)
        .then(res => {
          this.editedItem = res.data.comprobante;
          this.lineasDetalle = res.data.comprobanteDetalle;
          this.updateUIVisibility();
          if (this.editedItem.clienteCedula) {
            this.getActividadesCliente(this.editedItem.clienteCedula);
          }
        });
    },

    updateUIVisibility() {
      const status = this.editedItem.estadoHacienda;
      const finished = status === "ACEPTADO" || status === "RECHAZADO";
      this.imprimirValid = finished;
      this.reenviarValid = finished;
      this.verPDF = finished;
      this.verXMLFirmado = finished;
      this.verXMLRespuesta = finished;
      this.consultarEstadoValid = (status === "PROCESANDO");
    },

    detalleEditItem(item) {
      this.detalleEditedIndex = this.lineasDetalle.indexOf(item);
      this.detalleEditedItem = Object.assign({}, item);
      this.dialogArticulos = true;
    },

    deleteItem(item) {
      this.detalleEditedIndex = this.lineasDetalle.indexOf(item);
      this.dialogDelete = true;
    },

    deleteItemConfirm() {
      this.lineasDetalle.splice(this.detalleEditedIndex, 1);
      this.recalcularTotales();
      this.closeDelete();
    },

    recalcularTotales() {
      axios.post("api/Facturacion/realizarCalculosComprobante", this.lineasDetalle)
        .then(res => {
          this.lineasDetalle = res.data.comprobanteDetalle;
          const t = res.data.comprobante;
          Object.assign(this.editedItem, {
            subtotal: t.subtotal, 
            totalDescuentos: t.totalDescuentos, 
            totalImpuesto: t.totalImpuesto,
            totalIva1: t.totalIva1, 
            totalIva2: t.totalIva2, 
            totalIva4: t.totalIva4, 
            totalIva8: t.totalIva8,
            totalIva13: t.totalIva13,
            totalComprobante: t.totalComprobante, 
            subtotalStr: t.subtotalStr, 
            totalDescuentosStr: t.totalDescuentosStr,
            totalImpuestoStr: t.totalImpuestoStr, 
            totalComprobanteStr: t.totalComprobanteStr,
            totalIva1Str: t.totalIva1Str, 
            totalIva2Str: t.totalIva2Str, 
            totalIva4Str: t.totalIva4Str, 
            totalIva13Str: t.totalIva13Str, 
            totalIVADevueltoStr: t.totalIVADevueltoStr,
            totalServGravados: t.totalServGravados,
            totalServExentos: t.totalServExentos,
            totalServExonerado: t.totalServExonerado,
            totalMercanciasGravadas: t.totalMercanciasGravadas,
            totalMercanciasExentas: t.totalMercanciasExentas,
            totalMercExonerada: t.totalMercExonerada,
            totalGravado: t.totalGravado,
            totalExento: t.totalExento,
            totalExonerado: t.totalExonerado,
            totalVenta: t.totalVenta,
            totalVentaNeta: t.totalVentaNeta,
            totalIVADevuelto: t.totalIVADevuelto,
            totalOtrosCargos: t.totalOtrosCargos
          });
        });
    },

    saveDetalleArticulo() {
      if (this.detalleEditedIndex > -1) {
        Object.assign(this.lineasDetalle[this.detalleEditedIndex], this.detalleEditedItem);
      } else {
        this.lineasDetalle.push(this.detalleEditedItem);
      }
      this.dialogArticulos = false;
      this.recalcularTotales();
      this.detalleEditedIndex = -1;
    },

    save() {
      if (!this.editedItem.clienteCod || this.lineasDetalle.length === 0) {
        this.snackText = "Complete el cliente y agregue artículos";
        this.snackValue = true;
        return;
      }
      this.visibleProgress = true;
      
      // Asegurar fechas actuales al momento de insertar
      const now = new Date();
      this.editedItem.fecha = now;
      this.editedItem.fechaHacienda = now;
      
      // Garantizar que contingenciaFecha siempre tenga un valor válido para el backend (.NET DateTime)
      // Pero preservamos el valor si el usuario lo ingresó manualmente para una contingencia
      if (!this.editedItem.contingenciaFecha) {
        this.editedItem.contingenciaFecha = now;
      }
      
      this.editedItem.cia = this.strCia;
      this.editedItem.usuario = this.user.id;
      this.editedItem.tipoFact = "01";
      
      const payload = {
        comprobante: this.editedItem,
        comprobanteDetalle: this.lineasDetalle
      };

      axios.post("api/Facturacion/insertComprobante", payload)
        .then(res => {
          this.editedItem.clavehacienda = res.data.claveHacienda;
          this.editedItem.estadoHacienda = res.data.eInvoiceStatus;
          this.editedItem.mensajeHacienda = res.data.details;
          this.updateUIVisibility();
          this.getData();
          this.guardarValid = false;
        })
        .finally(() => this.visibleProgress = false);
    },

    consultarEstadoHacienda() {
      this.visibleProgress = true;
      this.editedItem.nombreTabla = "Facturas";
      axios.post("api/Facturacion/obtenerRespuestaHacienda", this.editedItem)
        .then(res => {
          this.editedItem.estadoHacienda = res.data.eInvoiceStatus;
          this.editedItem.mensajeHacienda = res.data.details;
          this.updateUIVisibility();
          this.getData();
        })
        .finally(() => this.visibleProgress = false);
    },

    validarLicencia() {
      axios.get("api/General/licenciaValidar?cia=" + this.strCia)
        .then(res => res.data.valido ? this.incluir() : (this.snackText = res.data.motivo, this.snackValue = true));
    },

    incluir() {
      this.editedIndex = -1;
      this.editedItem = this.getInitialItem();
      this.lineasDetalle = [];
      this.actividadesCliente = [];
      this.drawerForm = true;
      this.activePanels = [0, 1];
      this.btnAgregarCliente = true;
      this.btnAgregarArti = false;
      this.guardarValid = true;
      this.incluirValid = true;
      this.getCia();
      this.getCodigoFactura();
      this.setDefaults();
    },

    setDefaults() {
       if (this.condicionesVenta.length > 0) this.editedItem.condicionVenta = this.condicionesVenta[0].codigo;
       if (this.mediosPago.length > 0) this.editedItem.medioPago = this.mediosPago[0].codigo;
       if (this.monedas.length > 0) this.editedItem.moneda = this.monedas[0].codigo || "CRC";
    },

    getCodigoFactura() {
      axios.get(`api/Facturacion/getComprobanteConsInterno?cia=${this.strCia}&docNombre=Facturas&tipoDoc=F`)
        .then(res => this.editedItem.codigoFact = res.data);
    },

    cerrar() {
      this.drawerForm = false;
      this.editedIndex = -1;
      this.editedItem = this.getInitialItem();
      this.lineasDetalle = [];
      this.actividadesCliente = [];
    },
    closeDelete() { this.dialogDelete = false; },
    closed() { 
      this.showNewData = false; 
      this.showNewDataArti = false; 
      this.dialog = false; 
      this.dialogBuscarArticulos = false;
      this.dialogSinpe = false; 
      this.dialogFormaReenvio = false;
    },

    selected(cliente) { 
      if (!cliente) return;
      this.applyCliente(cliente); 
      this.drawerForm = true; 
      this.closed();
    },
    selectedArticulos(art) {
      if (!art) return;
      const newLine = {
        cia: this.strCia, factura: this.editedItem.codigoFact, tipoFact: "01", numLinea: this.lineasDetalle.length + 1,
        cantidad: 1, articulo: art.codigo, descripcion: art.nombre, unidad: art.unidad, monto: art.monto,
        descuento: 0, cabys: art.cabys, impCodigo: art.codigoImp, impCodigoTarifa: art.codigoTarifa,
        impTarifa: art.tarifa, impuestosVentas: art.tarifa, porcDescuento: 0, tipoCambio: this.editedItem.tipoCambio, 
        tipoProducto: art.tipoArt, moneda: this.editedItem.moneda,
        clienteCod: this.editedItem.clienteCod, clienteCedula: this.editedItem.clienteCedula,
        sucursal: this.editedItem.sucursal, terminal: this.editedItem.terminal,
        tipoCodigo: "01", unidadMedidaComercial: "M2", impuestoNeto: 0, totalLinea: art.monto,
        montoStr: art.monto.toLocaleString('es-CR', { style: 'currency', currency: 'CRC' }), 
        totalLineaStr: art.monto.toLocaleString('es-CR', { style: 'currency', currency: 'CRC' }),
        registroMedicamento: art.registroMedicamento, formaFarmaceutica: art.formaFarmaceutica
      };
      this.lineasDetalle.push(newLine);
      this.drawerForm = true; 
      this.closed();
      this.recalcularTotales();
    },

    openDocViewer(url, title) {
      this.docViewerUrl = url;
      this.docViewerTitle = title;
      this.docViewerDialog = true;
    },

    abrirEnNuevaPestana() {
      window.open(this.docViewerUrl, '_blank');
    },

    imprimir() {
      const base = axios.defaults.baseURL;
      this.openDocViewer(
        `${base}/api/Facturacion/getFileContent?cia=${this.strCia}&clave=${this.editedItem.clavehacienda}&type=pdf`,
        "PDF — Factura Electrónica"
      );
    },

    getXMLFirmado() {
      const base = axios.defaults.baseURL;
      this.openDocViewer(
        `${base}/api/Facturacion/getFileContent?cia=${this.strCia}&clave=${this.editedItem.clavehacienda}&type=signed`,
        "XML Enviado — Factura Electrónica"
      );
    },

    getXMLRespuesta() {
      const base = axios.defaults.baseURL;
      this.openDocViewer(
        `${base}/api/Facturacion/getFileContent?cia=${this.strCia}&clave=${this.editedItem.clavehacienda}&type=response`,
        "Respuesta XML — Factura Electrónica"
      );
    },

    reenviar() {
      this.reenvioInfo.correo = this.editedItem.clientecorreo;
      this.dialogFormaReenvio = true;
    },

    confirmarReenvio() {
      this.visibleProgress = true;
      this.editedItem.clientecorreo = this.reenvioInfo.correo;
      axios.post("api/Facturacion/reenviarCorreo", this.editedItem)
        .then(res => {
          this.snackText = res.data;
          this.snackValue = true;
          this.dialogFormaReenvio = false;
        })
        .finally(() => this.visibleProgress = false);
    },

    getOpcionesSistemaCia() {
      axios.get("api/General/getOpcionesSistemaCia?cia=" + this.strCia)
        .then(res => this.opcionesSistemaCia = res.data);
    },

    getFormas() {
      axios.get("api/General/getFormaFarmaceutica").then(res => { this.formas = res.data; });
    },
  }
};
</script>

<style scoped>
/* ===== CONTAINER ===== */
.fe-container { background-color: #f1f5f9; min-height: 100vh; }
.theme--dark.fe-container { background-color: #0f172a; }

/* ===== TOOLBAR ===== */
.fe-toolbar { border-bottom: 1px solid rgba(0,0,0,0.1) !important; }
.theme--dark .fe-toolbar { border-bottom: 1px solid rgba(255,255,255,0.07) !important; }
.fe-toolbar-title { font-size: 0.95rem !important; letter-spacing: 1.5px; }

/* ===== BUTTONS ===== */
.fe-btn { border-radius: 4px !important; text-transform: uppercase; letter-spacing: 0.8px; font-size: 0.75rem !important; }

/* ===== TABLE ===== */
.fe-table-card { border-bottom: 1px solid rgba(0,0,0,0.08) !important; }
.fe-table { background: transparent !important; }
.fe-table >>> thead th {
  background-color: #37474f !important;
  color: #ffffff !important;
  text-transform: uppercase;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.07em;
  border-bottom: none !important;
}
.fe-table >>> tbody tr { cursor: pointer; }
.fe-table >>> tbody tr:hover { background-color: rgba(55,71,79,0.05) !important; }
.theme--dark .fe-table >>> tbody tr:hover { background-color: rgba(255,255,255,0.05) !important; }
.fe-search >>> .v-input__slot { border-radius: 4px !important; }

/* ===== DRAWER ===== */
.fe-drawer >>> .v-navigation-drawer__content { background-color: #ffffff !important; }
.theme--dark .fe-drawer >>> .v-navigation-drawer__content { background-color: #0f172a !important; }
.fe-drawer-header { background-color: #37474f; flex-shrink: 0; }
.fe-drawer-header--dark { background-color: #263238 !important; }
.fe-drawer-body { background-color: #f8fafc !important; }
.theme--dark .fe-drawer-body { background-color: #0f172a !important; }
.fe-drawer-footer { background-color: #f8fafc; border-top: 1px solid rgba(0,0,0,0.08); flex-shrink: 0; }
.fe-drawer-footer--dark { background-color: #1e293b !important; border-top: 1px solid rgba(255,255,255,0.07) !important; }

/* ===== DIALOG ===== */
.fe-dialog-card { border-radius: 4px !important; overflow: hidden; }

/* ===== EXPANSION PANELS ===== */
.fe-panels >>> .v-expansion-panel { border: 1px solid rgba(0,0,0,0.07) !important; border-radius: 4px !important; }
.fe-panels >>> .v-expansion-panel--active { border-color: rgba(55,71,79,0.3) !important; margin-bottom: 2px !important; }
.fe-panels >>> .v-expansion-panel + .v-expansion-panel { margin-top: 2px !important; }
.fe-panels >>> .v-expansion-panel-header { min-height: 40px !important; padding: 0 12px !important; font-size: 0.75rem !important; }
.fe-panels >>> .v-expansion-panel-content__wrap { padding: 4px 4px 6px !important; }
.theme--dark .fe-panels >>> .v-expansion-panel { border-color: rgba(255,255,255,0.07) !important; }

.fe-panel-header { letter-spacing: 1px !important; }
.fe-panel-header :deep(.v-icon) { color: #37474f !important; }
.theme--dark .fe-panel-header :deep(.v-icon) { color: #b0bec5 !important; }

/* ===== TOTALES ===== */
.fe-total-card { border: 1px solid rgba(0,0,0,0.07) !important; border-radius: 4px !important; background-color: #f8fafc !important; }
.theme--dark .fe-total-card { background-color: #1e293b !important; border-color: rgba(255,255,255,0.07) !important; }
.fe-total-card--primary { background-color: var(--v-primary-base) !important; border-color: transparent !important; }

/* ===== DETAIL TABLE ===== */
.fe-detail-table { background: transparent !important; border: 1px solid rgba(0,0,0,0.07); border-radius: 4px; overflow: hidden; }
.fe-detail-table >>> thead th {
  background-color: #37474f !important;
  color: #ffffff !important;
  text-transform: uppercase;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.07em;
  border-bottom: none !important;
}
.edit-table >>> td { height: 40px !important; }

/* ===== COMPACT INPUTS (en tabla de detalle) ===== */
.compact-input >>> input {
  padding: 0px !important;
  text-align: center !important;
  font-weight: 500 !important;
  font-size: 0.9rem !important;
  height: 28px !important;
  min-width: 40px !important;
  max-width: 60px !important;
}
.compact-input >>> .v-input__slot { padding: 0 4px !important; border-radius: 4px !important; min-height: 28px !important; }
.compact-input >>> .v-input__control { min-height: 38px !important; }
.compact-input >>> input::-webkit-outer-spin-button,
.compact-input >>> input::-webkit-inner-spin-button { -webkit-appearance: none !important; margin: 0 !important; }
.theme--dark .compact-input >>> input { color: #ffffff !important; }
.theme--light .compact-input >>> input { color: #000000 !important; }

.right-input >>> input { text-align: right; font-weight: bold; }
</style>